export const es = {
  locale: 'es-ES',
  app: {
    name: 'Routecast',
    tagline: 'El tiempo en cada tramo de tu ruta, a la hora en que pases.',
    building: 'En construcción. Mientras tanto, mira la galería de componentes.',
    gallery: 'Galería de componentes',
  },
  risk: {
    levels: ['Sin riesgo', 'Precaución', 'Alto', 'Severo'],
    short: ['Bajo', 'Precaución', 'Alto', 'Severo'],
    unknown: 'Sin datos',
    legend: 'Leyenda de riesgo',
    legendNote:
      'Umbrales orientativos. «Tormenta con granizo» es un riesgo estimado, no una certeza. Consulta los avisos oficiales antes de salir.',
  },
  factor: {
    rain: 'Lluvia',
    crosswind: 'Viento cruzado',
    gust: 'Rachas',
    snow: 'Nieve',
    ice: 'Hielo',
    visibility: 'Visibilidad',
    storm: 'Tormenta',
  },
  /** Qué es el peligro, en una o dos palabras (junto al nivel: «Alto · Poca visibilidad»). */
  cause: {
    rain: 'Lluvia fuerte',
    crosswind: 'Viento lateral',
    gust: 'Rachas de viento',
    snow: 'Nieve',
    ice: 'Hielo',
    visibility: 'Poca visibilidad',
    storm: 'Tormenta',
    hail: 'Tormenta (posible granizo)',
  },
  picker: {
    label: 'Hora de salida',
    days: 'Día de salida',
    today: 'Hoy',
    tomorrow: 'Mañana',
    orientative: 'previsión orientativa',
    orientativeNote: 'Previsión orientativa (más de 48 h)',
    now: 'Ahora',
    nowMark: 'ahora',
    later: (d: string) => `+${d}`,
    hint: 'La fila elige el día. Arrastra el dial o usa las flechas (10 min), Re Pág / Av Pág (1 h), Inicio (ahora) y Fin (última hora).',
    valueText: (when: string, route: string, level: string) => `Salida: ${when}. Ruta ${route}: ${level}.`,
  },
  roadwind: {
    title: 'Viento respecto a la carretera',
    north: 'N',
    summary: (heading: number, from: number, gust: number, cross: number) =>
      `Carretera hacia ${heading}°, viento del ${from}°. De ${gust} km/h de racha, ${cross} km/h empujan de lado.`,
    calm: 'Viento en calma.',
    cross: 'cruzado',
    head: 'de cara',
    tail: 'de cola',
  },
  compare: {
    caption: 'Rutas ordenadas por riesgo',
    route: 'Ruta',
    time: 'Tiempo',
    worst: 'Riesgo',
    high: 'Km riesgo',
    arrival: (t: string) => `llegada ${t}`,
    noRisk: 'Sin tramos con riesgo a esta hora',
    pending: 'Calculando…',
    dataUntil: (km: number) => `Datos hasta km ${km}`,
    kmHigh: (km: number) => `${km} km en riesgo alto o severo`,
  },
  profile: {
    label: (route: string) => `Perfil de riesgo de la ruta ${route}`,
    at: (km: number, level: string) => `km ${km}: ${level}`,
    idle: 'Usa las flechas para recorrer la ruta por kilómetros.',
    range: (from: number, to: number) => `km ${from}–${to}`,
    high: (ranges: string) => `Riesgo alto o severo en ${ranges}.`,
  },
  search: {
    from: 'Origen',
    to: 'Destino',
    placeholder: 'Ciudad, dirección o lugar',
    recent: 'Recientes',
    noResults: (q: string) => `Nada encontrado para «${q}». Prueba con el nombre del municipio.`,
    loading: 'Buscando…',
    error: 'El buscador no responde. Inténtalo de nuevo en unos segundos.',
    swap: 'Intercambiar origen y destino',
    clear: 'Borrar',
  },
  sheet: {
    handle: 'Panel de rutas',
    expand: 'Ampliar panel',
    collapse: 'Reducir panel',
  },
  alerts: {
    title: 'Avisos oficiales',
    count: (n: number) => (n === 1 ? '1 aviso en tu ruta' : `${n} avisos en tu ruta`),
    none: 'Sin avisos oficiales en tu ruta para esa ventana.',
    notYet:
      'Los avisos oficiales no suelen publicarse con tanta antelación. Que no haya ninguno no significa que el día vaya a estar despejado.',
    window: (from: string, to: string) => `${from}–${to}`,
    levels: { yellow: 'Amarillo', orange: 'Naranja', red: 'Rojo' },
    source: (s: string) => `Fuente: ${s}`,
    partial: 'Algunos avisos no se han podido cargar.',
    unavailable: 'Los avisos oficiales no están disponibles ahora mismo: consulta aemet.es antes de salir.',
    loading: 'Consultando avisos oficiales…',
    stretch: (from: number, to: number, time: string) => `km ${from}–${to} · pasas hacia las ${time}`,
    onRoute: (n: number) => (n === 1 ? '1 aviso' : `${n} avisos`),
    kinds: {
      wind: 'Viento',
      rain: 'Lluvias',
      snow: 'Nevadas',
      storm: 'Tormentas',
      fog: 'Nieblas',
      heat: 'Temperaturas máximas',
      cold: 'Temperaturas mínimas',
      coastal: 'Fenómenos costeros',
      dust: 'Polvo en suspensión',
      other: 'Otros fenómenos',
    },
  },
  empty: {
    startTitle: '¿Adónde vas?',
    startBody: 'Elige origen y destino. Te mostramos hasta tres rutas y qué tiempo hará en cada tramo cuando pases.',
    noRouteTitle: 'Sin ruta en coche',
    noRouteBody: 'No hay ruta por carretera entre esos puntos. Revisa que ambos estén en tierra firme.',
    horizonTitle: 'Demasiado lejos en el tiempo',
    horizonBody: 'La previsión horaria llega hasta unos 7 días. Elige una salida más cercana.',
    offlineTitle: 'Sin conexión',
    offlineBody: 'Mostramos la última ruta guardada. Los datos pueden estar desactualizados.',
    retry: 'Reintentar',
  },
  toast: {
    rateLimited: 'Demasiadas consultas seguidas. Esperamos un momento y reintentamos.',
    weatherDown: 'Sin datos meteorológicos ahora mismo. La ruta se muestra sin riesgo calculado.',
    copied: 'Enlace copiado.',
    dismiss: 'Cerrar aviso',
  },
  theme: { label: 'Tema', system: 'Sistema', light: 'Claro', dark: 'Oscuro' },
  lang: { label: 'Idioma', switchTo: 'Switch to English', short: 'EN' },
  units: { km: 'km', kmh: 'km/h' },
  explain: {
    rain: (mm: string) => `Lluvia de ${mm} mm/h`,
    crosswind: (cross: number, gust: number) => `Racha cruzada de ${cross} km/h (racha ${gust})`,
    gust: (gust: number) => `Rachas de ${gust} km/h`,
    snow: (cm: string) => `Nieve de ${cm} cm/h`,
    ice: (temp: string) => `Posible hielo: ${temp} °C con precipitación`,
    freezing: 'Lluvia o llovizna engelante',
    visibility: (m: number) => `Visibilidad de ${m} m`,
    storm: 'Tormenta',
    hail: 'Riesgo de tormenta fuerte o con granizo',
    at: (time: string, km: number) => `${time} · km ${km}`,
    partial: (factors: string) => `Sin datos de ${factors} en algún tramo.`,
    beyondForecast: (km: number) =>
      `La previsión horaria no llega al final del viaje: desde el km ${km} no hay datos. Sal antes o elige otro día.`,
    unknown: 'Sin datos meteorológicos para ese momento.',
    upTo: (km: number) => `hasta ${km} km`,
  },
  status: {
    routingDownBody: 'Puede ser un corte momentáneo de los servidores públicos. Reintenta en unos segundos.',
    weatherPartial: 'Falta la previsión de algunos tramos: se muestran sin riesgo calculado.',
    routing: 'Buscando rutas…',
    weather: 'Consultando la previsión en cada tramo…',
    points: (done: number, total: number) => `${done} de ${total} puntos`,
    noRoute: 'No hay ruta en coche entre esos puntos.',
    routingDown: 'Los servidores de rutas no responden. Inténtalo de nuevo en un momento.',
    weatherDown: 'Sin previsión meteorológica ahora mismo: mostramos las rutas sin riesgo calculado.',
    rateLimited: 'Demasiadas consultas seguidas: espera unos segundos y reintenta.',
    retry: 'Reintentar',
    fallback: 'Usando el servidor de rutas de respaldo.',
    pickBoth: 'Elige origen y destino para comparar rutas.',
  },
  route: {
    label: (letter: string) => `Ruta ${letter}`,
    unnamed: 'Ruta sin nombre',
    via: (km: number) => `${km} km`,
    worstPoint: (route: string, time: string) => `Peor punto de la ruta ${route} · ${time}`,
    worstShort: (route: string) => `Peor punto · ruta ${route}`,
  },
  confidence: {
    low: 'A más de 48 h la previsión horaria es orientativa: el detalle puede cambiar.',
  },
  attribution:
    '© OpenStreetMap contributors · OpenFreeMap · Weather data by Open-Meteo.com (CC-BY 4.0) · Routing by Valhalla (FOSSGIS) / OSRM / OpenRouteService · Avisos: Fuente: AEMET · Meteoalarm (EUMETNET) · Lugares: Wikidata',
};

export type Dict = typeof es;
